import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import {
  boardFilterFacetSchema,
  boardFilterKeywordSchema,
  dueBucketSchema,
} from './board-filter';
import { customFieldValueSchema } from './custom-field';
import { ulidSchema } from './id';
import { cardLabelSchema } from './label';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';
import { publicProfileSchema } from './user';
import { WORKSPACE_NAME_MAX_LENGTH, WORKSPACE_SHORT_NAME_MAX_LENGTH } from './workspace';

/**
 * Table view contracts — block B50, amended by FE-56.2.
 *
 * These are route-specific read models. They deliberately do not reuse
 * `boardFaceCardSchema`: the table has fixed columns (card, list, labels,
 * members, due date, plus board for Workspace Table) and must not inherit board
 * face badges by accident.
 *
 * ⚠ CUSTOM FIELDS ARE NO LONGER "a future surface to be kept out" — they are a
 * BOARD-TABLE-ONLY surface, and the asymmetry is the whole point. The
 * 2026-09-06 corpus amendment settled both halves in one pass:
 * `FR-VIEW-012`'s behaviour now names "an optional column for each custom field
 * defined on that board", while `FR-VIEW-016` still says of the Workspace Table
 * that "descriptions and custom fields are not shown", and both requirements
 * carry a Settled edge case saying WHY: field definitions are board-scoped, so
 * one board has exactly one well-defined field set to build columns from and a
 * selection of up to 20 boards has none. Giving the workspace table columns
 * would mean inventing a live cross-board field-identity rule no requirement
 * defines.
 *
 * ⚠ THE SHAPE BELOW ENFORCES THAT, and it is the reason `boardTableSchema.rows`
 * does not point at `boardTableRowSchema`. See the comment on
 * `workspaceTableRowSchema`.
 */

export const WORKSPACE_TABLE_BOARD_LIMIT = 20;

function repeatedQueryValue(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return Array.from(value as unknown[]);
  return [value];
}

const ulidArrayQuerySchema = z.preprocess(repeatedQueryValue, z.array(ulidSchema));

export const tableViewListRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
});

export type TableViewListRef = z.infer<typeof tableViewListRefSchema>;

export const tableViewBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  closed: z.boolean(),
});

export type TableViewBoardRef = z.infer<typeof tableViewBoardRefSchema>;

export const tableViewWorkspaceRefSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
});

export type TableViewWorkspaceRef = z.infer<typeof tableViewWorkspaceRefSchema>;

export const tableViewCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN` for a viewer who may use the board's references, else `null` — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  dueAt: z.string().nullable(),
  /** `cards.due_all_day` — FR-EXP-029 edge case (BE-44.7). */
  dueAllDay: z.boolean(),
});

export type TableViewCardRef = z.infer<typeof tableViewCardRefSchema>;

export const boardTableRowSchema = z.object({
  card: tableViewCardRefSchema,
  list: tableViewListRefSchema,
  labels: z.array(cardLabelSchema),
  members: z.array(publicProfileSchema),
});

export type BoardTableRow = z.infer<typeof boardTableRowSchema>;

/**
 * ⚠ EXTENDS THE BARE `boardTableRowSchema`, AND MUST GO ON DOING SO.
 * `FR-VIEW-016` refuses custom fields; this row is the only thing that keeps
 * that refusal honest on the wire. Adding a `customFields` key to
 * `boardTableRowSchema` — the obvious "both tables share a base" edit — would
 * hand the Workspace Table the exact columns its own Settled edge case forbids,
 * silently, because nothing about the extension reads as a workspace change.
 *
 * ⚠ AND A SERVER-SIDE ASSERTION THAT THIS ROW CARRIES NO CUSTOM FIELDS PROVES
 * NOTHING: Zod strips unknown keys, so `workspaceTableRowSchema.parse(row)`
 * returns a row without them whether or not `readWorkspaceTable` projected any.
 * The discriminating pin lives where the columns are DRAWN, in
 * `WorkspaceTablePage.test.tsx`, which hands the page a row that does carry the
 * key and asserts the header set is unchanged.
 */
export const workspaceTableRowSchema = boardTableRowSchema.extend({
  board: tableViewBoardRefSchema,
});

export type WorkspaceTableRow = z.infer<typeof workspaceTableRowSchema>;

/**
 * One board table row — `FR-VIEW-012`, the single-board table and nowhere else.
 *
 * ⚠ `customFields` holds only the values a card actually carries, each already
 * keyed by its own `definitionId` (`customFieldValueSchema` carries it), so an
 * empty array is the normal shape rather than a degraded one: `FR-VIEW-012`
 * promises the view has "no data precondition — a card with no dates, members,
 * labels or custom field values still appears, with empty cells". The COLUMNS
 * are not in this payload at all; they come from the board's definitions
 * (`GET /boards/:id/custom-fields`, `customFieldDefinitionSchema`), which is the
 * one projection of them and is guarded identically to the table route. That
 * also fixes the column ORDER at the definitions' own `position`, which
 * `FR-SEARCH-031` asks for by the same words.
 *
 * ⚠ HIDDEN DEFINITIONS ARE NOT PROJECTED HERE — see `readTableCustomFieldValues`
 * in `apps/server/src/custom-fields/read.ts` for the ruling and its reason.
 */
export const boardTableCustomFieldRowSchema = boardTableRowSchema.extend({
  customFields: z.array(customFieldValueSchema),
});

export type BoardTableCustomFieldRow = z.infer<typeof boardTableCustomFieldRowSchema>;

export const boardTableSchema = z.object({
  board: tableViewBoardRefSchema,
  rows: z.array(boardTableCustomFieldRowSchema),
});

export type BoardTable = z.infer<typeof boardTableSchema>;

export const workspaceTableSchema = z.object({
  workspace: tableViewWorkspaceRefSchema,
  selectedBoardIds: z.array(ulidSchema).max(WORKSPACE_TABLE_BOARD_LIMIT),
  rows: z.array(workspaceTableRowSchema),
});

export type WorkspaceTable = z.infer<typeof workspaceTableSchema>;

export const workspaceTableQuerySchema = z.strictObject({
  boardIds: ulidArrayQuerySchema
    .pipe(z.array(ulidSchema).min(1).max(WORKSPACE_TABLE_BOARD_LIMIT))
    .describe(
      'Selected board ids for this Workspace Table request. Repeat the key for ' +
        'several boards; 1 to 20 are accepted.',
    ),
  members: boardFilterFacetSchema.optional(),
  labels: boardFilterFacetSchema.optional(),
  lists: ulidArrayQuerySchema.optional(),
  due: z.preprocess(repeatedQueryValue, z.array(dueBucketSchema)).optional(),
  keyword: boardFilterKeywordSchema.optional(),
});

export type WorkspaceTableQuery = z.infer<typeof workspaceTableQuerySchema>;
